import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import { alpha } from "@mui/material/styles";
import { DataUsageRounded } from "@mui/icons-material";

const MotionBox = motion(Box);

const SECTION_CARD_SX = {
    display: "flex",
    flexDirection: "column",
    borderRadius: "12px",
    position: "relative",
    overflow: "hidden",
    background: `
        linear-gradient(
            180deg,
            rgba(12,12,14,0.96) 0%,
            rgba(8,8,10,0.985) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.05)",
    backdropFilter: "blur(18px)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.05),
        inset 0 -40px 80px rgba(246,185,76,0.03),
        0 24px 60px rgba(0,0,0,0.42)
    `,
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
};

const CARD_TOP_GLOW_SX = {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "42%",
    height: "1px",
    background: `
        linear-gradient(
            90deg,
            transparent,
            rgba(246,185,76,0.95),
            transparent
        )
    `,
    boxShadow: "0 0 18px rgba(246,185,76,0.45)",
    pointerEvents: "none",
};

function EmptyBody({ title, description, Icon, compact }) {
    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: compact ? "1.25rem" : "2rem",
                py: compact ? "1.5rem" : "2rem",
                width: "100%",
            }}
        >
            <Box sx={{ position: "relative", mb: compact ? "0.85rem" : "1rem" }}>
                <Box
                    sx={{
                        position: "absolute",
                        inset: "-12px",
                        borderRadius: "50%",
                        background: `
                            radial-gradient(
                                circle,
                                rgba(246,185,76,0.14),
                                transparent 70%
                            )
                        `,
                        filter: "blur(12px)",
                    }}
                />
                <Icon
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        fontSize: compact ? "1.75rem" : "2rem",
                        color: alpha(colors.primary.main, 0.9),
                        filter: "drop-shadow(0 0 12px rgba(246,185,76,0.18))",
                    }}
                />
            </Box>

            <Typography
                sx={{
                    fontSize: compact ? "0.78rem" : "0.82rem",
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.9)",
                    mb: "0.45rem",
                }}
            >
                {title}
            </Typography>

            {description ? (
                <Typography
                    sx={{
                        fontSize: compact ? "0.8rem" : "0.84rem",
                        fontWeight: 500,
                        color: "rgba(255,255,255,0.42)",
                        lineHeight: 1.6,
                        maxWidth: "22rem",
                    }}
                >
                    {description}
                </Typography>
            ) : null}
        </Box>
    );
}

EmptyBody.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.node,
    Icon: PropTypes.elementType.isRequired,
    compact: PropTypes.bool,
};

/**
 * AAA empty state.
 * - Default: standalone card
 * - With `sectionTitle`: FACEIT-style section card (header + empty body)
 * - With `embedded`: body only (use inside an existing card)
 */
export function EmptyDataState({
    children,
    title = "No Data Available",
    sectionTitle,
    sectionSubtitle,
    minHeight = "10rem",
    sx,
    icon: Icon = DataUsageRounded,
    embedded = false,
}) {
    const description = children;
    const compact = Boolean(sectionTitle) || embedded;

    if (embedded) {
        return (
            <Box
                sx={{
                    width: "100%",
                    minHeight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ...sx,
                }}
            >
                <EmptyBody
                    title={title}
                    description={description}
                    Icon={Icon}
                    compact
                />
            </Box>
        );
    }

    if (sectionTitle) {
        return (
            <MotionBox
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                sx={{
                    ...SECTION_CARD_SX,
                    minHeight,
                    ...sx,
                }}
            >
                <Box sx={CARD_TOP_GLOW_SX} />
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        px: "1.5rem",
                        pt: "1.25rem",
                        pb: "0.85rem",
                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.72rem",
                            fontWeight: 800,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: colors.text.primary,
                            lineHeight: 1,
                        }}
                    >
                        {sectionTitle}
                    </Typography>
                    {sectionSubtitle ? (
                        <Typography
                            sx={{
                                mt: "0.35rem",
                                fontSize: "0.68rem",
                                fontWeight: 500,
                                letterSpacing: "0.04em",
                                color: "rgba(255,255,255,0.42)",
                                lineHeight: 1.35,
                            }}
                        >
                            {sectionSubtitle}
                        </Typography>
                    ) : null}
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 0,
                    }}
                >
                    <EmptyBody
                        title={title}
                        description={description}
                        Icon={Icon}
                        compact
                    />
                </Box>
            </MotionBox>
        );
    }

    return (
        <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
                duration: 0.15,
                ease: "easeInOut",
            }}
            sx={{
                position: "relative",
                width: "100%",
                maxWidth: "100%",
                minHeight,
                overflow: "hidden",
                borderRadius: "18px",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `
                    linear-gradient(
                        180deg,
                        rgba(16,18,20,0.94) 0%,
                        rgba(10,12,14,0.98) 100%
                    )
                `,
                border: "1px solid rgba(255,255,255,0.045)",
                backdropFilter: "blur(18px)",
                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.04),
                    inset 0 -40px 80px rgba(246,185,76,0.025)
                `,
                ...sx,
            }}
        >
            <Box sx={CARD_TOP_GLOW_SX} />
            <MotionBox
                animate={{
                    opacity: [0.16, 0.28, 0.16],
                    scale: [1, 1.04, 1],
                }}
                transition={{
                    repeat: Infinity,
                    duration: 6,
                    ease: "easeInOut",
                }}
                sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: 0,
                    transform: "translateX(-50%)",
                    width: "100%",
                    height: "75%",
                    borderRadius: "50%",
                    background: `
                        radial-gradient(
                            ellipse at bottom,
                            rgba(246,185,76,0.12) 0%,
                            rgba(246,185,76,0.04) 35%,
                            transparent 72%
                        )
                    `,
                    filter: "blur(40px)",
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0.03,
                    backgroundImage: `
                        radial-gradient(
                            rgba(255,255,255,0.8) 0.4px,
                            transparent 0.4px
                        )
                    `,
                    backgroundSize: "6px 6px",
                    pointerEvents: "none",
                }}
            />
            <EmptyBody title={title} description={description} Icon={Icon} />
        </MotionBox>
    );
}

EmptyDataState.propTypes = {
    children: PropTypes.node,
    title: PropTypes.string,
    sectionTitle: PropTypes.string,
    sectionSubtitle: PropTypes.string,
    minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    sx: PropTypes.object,
    icon: PropTypes.elementType,
    embedded: PropTypes.bool,
};
